import React from 'react';

import { Divider } from '@mui/material';
import TextField from 'components/FormElements/AppTextInput';
import { dataRoomStatusOptions } from 'further-ui/labels';
import { DataRoomFormData } from '../formData';
import { Control, Controller } from 'react-hook-form';
import FormField from 'further-ui/components/FormField';
import BasicSelect from 'further-ui/components/BasicSelect';
import ContactImageUpload from './ContactImageUpload';
import { Column } from 'further-ui/components/Layout';
import KeyValueList from 'components/new/KeyValueList';

type Props = {
  control: Control<DataRoomFormData>;
  setLiveDisabled?: boolean;
};

const DataRoomGeneral: React.FC<Props> = ({ control, setLiveDisabled }) => {
  return (
    <Column spacing="lg">
      <FormField
        tooltip={
          setLiveDisabled
            ? 'To make this data room live, complete the Setup and Contents sections and move the associated tranche out of Draft status.'
            : undefined
        }
        label="Data room status"
        size="md"
      >
        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <BasicSelect
              {...field}
              disabled={setLiveDisabled}
              fullWidth
              options={dataRoomStatusOptions}
            />
          )}
        />
      </FormField>
      <Divider flexItem />
      <FormField label="Data room headline" size="md">
        <Controller
          name="headline"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              placeholder="Enter headline"
              size="small"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="Data room subheading" size="md">
        <Controller
          name="subheading"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              multiline
              rows={5}
              size="small"
              placeholder="Enter subheading"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <Divider flexItem />
      <FormField
        label="Contact"
        extraInformation="Add the main point of contact for this tranche. Their details will be shown in your data room."
        size="md"
      >
        <Column spacing="sm">
          <ContactImageUpload control={control} />
          <Controller
            name="contact.name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                size="small"
                placeholder="Enter name"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <Controller
            name="contact.email"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                size="small"
                placeholder="Enter email address"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <Controller
            name="contact.phone"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                size="small"
                placeholder="Enter phone number"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Column>
      </FormField>
      <FormField
        label="Key information"
        extraInformation="Add details like fund size, minimum commitment or strategy. This information will show in your data room page."
      >
        <Controller
          name="keyInformation"
          control={control}
          render={({ field }) => (
            <KeyValueList
              value={field.value}
              onChange={(items) => {
                field.onChange(items);
              }}
            />
          )}
        />
      </FormField>
    </Column>
  );
};

export default DataRoomGeneral;
